import React from 'react';
import PropTypes from 'prop-types';
import { useUserSegmentationAnalytics } from '../../hooks/useAnalytics';
import MetricCard from '../dashboard/core/MetricCard';
import MetricSection from '../dashboard/core/MetricSection';
import CollapsibleSection from '../ui/CollapsibleSection';
import { exportSegment } from '../../utils/exportService';
import { formatPercent } from '../../utils/format';
import { FILTER_PERIODS } from '../../constants';
import './UserSegments.css';

/**
 * User Segments Component
 * Displays user segments by engagement, lifecycle, feature usage, and value
 */
const UserSegments = ({ period = FILTER_PERIODS.ALL }) => {
  const { data, loading, error } = useUserSegmentationAnalytics(period);

  if (error) {
    return (
      <div className="user-segments error">
        <h3>Error loading user segments</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-segments metric-loading" aria-live="polite" aria-busy="true">
        <div className="loading-spinner" />
        <p>Loading user segments...</p>
      </div>
    );
  }

  if (!data || !data.segments) {
    return (
      <div className="user-segments no-data-message">
        <i className="bi bi-info-circle icon-brand"></i>
        <p>No user segment data available for this period.</p>
      </div>
    );
  }

  const { segments, summary } = data;

  // Engagement metrics
  const engagementMetrics = [
    {
      id: 'power-users',
      title: 'Power Users',
      value: summary.powerUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.powerUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'green'
    },
    {
      id: 'active-users',
      title: 'Active Users',
      value: summary.activeUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.activeUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'blue'
    },
    {
      id: 'casual-users',
      title: 'Casual Users',
      value: summary.casualUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.casualUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'cyan'
    },
    {
      id: 'dormant-users',
      title: 'Dormant Users',
      value: summary.dormantUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.dormantUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'gray'
    }
  ];

  // Lifecycle metrics
  const lifecycleMetrics = [
    {
      id: 'new-users',
      title: 'New Users',
      value: summary.newUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.newUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'green'
    },
    {
      id: 'at-risk-users',
      title: 'At-Risk Users',
      value: summary.atRiskUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.atRiskUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'orange'
    },
    {
      id: 'churned-users',
      title: 'Churned Users',
      value: summary.churnedUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.churnedUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'red'
    }
  ];

  // Feature usage metrics
  const featureMetrics = [
    {
      id: 'split-creators',
      title: 'Split Creators',
      value: summary.splitCreators,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.splitCreators / summary.totalUsers) * 100 : 0, 1),
      color: 'purple'
    },
    {
      id: 'badge-earners',
      title: 'Badge Earners',
      value: summary.badgeEarners,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.badgeEarners / summary.totalUsers) * 100 : 0, 1),
      color: 'yellow'
    },
    {
      id: 'wallet-users',
      title: 'Wallet Users',
      value: summary.walletUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.walletUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'teal'
    }
  ];

  // Value metrics
  const valueMetrics = [
    {
      id: 'high-value',
      title: 'High-Value Users',
      value: summary.highValueUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.highValueUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'green'
    },
    {
      id: 'medium-value',
      title: 'Medium-Value Users',
      value: summary.mediumValueUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.mediumValueUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'blue'
    },
    {
      id: 'low-value',
      title: 'Low-Value Users',
      value: summary.lowValueUsers,
      formatType: 'number',
      subtitle: formatPercent(summary.totalUsers > 0 ? (summary.lowValueUsers / summary.totalUsers) * 100 : 0, 1),
      color: 'gray'
    }
  ];

  const handleExport = (segmentName, segmentData, format = 'csv') => {
    exportSegment(segmentData, segmentName, format);
  };

  return (
    <div className="user-segments">
      <div className="segments-summary">
        <MetricCard
          title="Total Users"
          value={summary.totalUsers}
          formatType="number"
          color="blue"
          loading={loading}
        />
      </div>

      <div className="segments-grid">
        <MetricSection
          title="By Engagement Level"
          icon="bi-graph-up"
          description="User activity and engagement patterns"
          metrics={engagementMetrics}
          loading={loading}
        />

        <MetricSection
          title="By Lifecycle Stage"
          icon="bi-arrow-repeat"
          description="User progression through lifecycle stages"
          metrics={lifecycleMetrics}
          loading={loading}
        />

        <MetricSection
          title="By Feature Usage"
          icon="bi-star"
          description="Users segmented by feature adoption"
          metrics={featureMetrics}
          loading={loading}
        />

        <MetricSection
          title="By Value"
          icon="bi-cash-coin"
          description="Users segmented by transaction value"
          metrics={valueMetrics}
          loading={loading}
        />
      </div>

      <div className="segments-export">
        <h3>Export Segments</h3>
        <div className="export-buttons">
          <button
            className="admin-btn-secondary"
            onClick={() => handleExport('power-users', segments.byEngagement.powerUsers, 'csv')}
            disabled={loading || segments.byEngagement.powerUsers.length === 0}
          >
            <i className="bi bi-download icon-brand"></i>
            Export Power Users (CSV)
          </button>
          <button
            className="admin-btn-secondary"
            onClick={() => handleExport('active-users', segments.byEngagement.activeUsers, 'csv')}
            disabled={loading || segments.byEngagement.activeUsers.length === 0}
          >
            <i className="bi bi-download icon-brand"></i>
            Export Active Users (CSV)
          </button>
          <button
            className="admin-btn-secondary"
            onClick={() => handleExport('at-risk-users', segments.byLifecycle.atRiskUsers, 'csv')}
            disabled={loading || segments.byLifecycle.atRiskUsers.length === 0}
          >
            <i className="bi bi-download icon-brand"></i>
            Export At-Risk Users (CSV)
          </button>
          <button
            className="admin-btn-secondary"
            onClick={() => handleExport('high-value-users', segments.byValue.highValue, 'csv')}
            disabled={loading || segments.byValue.highValue.length === 0}
          >
            <i className="bi bi-download icon-brand"></i>
            Export High-Value Users (CSV)
          </button>
        </div>
      </div>
    </div>
  );
};

UserSegments.propTypes = {
  period: PropTypes.string.isRequired
};

export default UserSegments;

